import { useEffect, useRef } from 'react'
import {
  assertReportRequest,
  reportRequestContext,
} from '../../lib/problem-reporting/context'
import { PexIcon } from '../../components/icons/PexIcon'
import { Button } from '../../components/ui/button'
import { useT } from '../../i18n'
import { reportCopy } from './copy'
import { ReportImages, type Submission } from './ReportImages'
import type { SubmissionState } from './submission'
export const reportInputClass =
  'w-full rounded-[var(--radius-control)] border border-[var(--color-border-strong)] bg-[var(--color-surface)] p-3 text-sm leading-6 placeholder:text-[var(--color-text-muted)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]'
export default function ReportForm({
  controller,
  state,
  available,
  approved,
  onApproval,
  onView,
  onRecord,
  recordingBusy,
  recordingErrorMessage,
}: {
  controller: Submission
  state: SubmissionState
  available: boolean
  approved: ReadonlySet<string>
  onApproval: (url: string, checked: boolean) => void
  onView: (id: string) => void
  onRecord: () => void
  recordingBusy: boolean
  recordingErrorMessage: string | null
}) {
  const alive = useRef(true)
  const descriptionRef = useRef<HTMLTextAreaElement>(null)
  const invalidDescription = state.error?.code === 'invalid_description'
  useEffect(() => {
    if (invalidDescription) descriptionRef.current?.focus()
  }, [invalidDescription])
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])
  const t = useT(reportCopy)
  const editable = available && ['draft', 'rejected'].includes(state.outcome)
  return (
    <div
      className="space-y-5"
      onPaste={async (event) => {
        if (!editable || state.preparing) return
        const files = Array.from(event.clipboardData.items)
          .filter(
            (item) => item.kind === 'file' && item.type.startsWith('image/'),
          )
          .map((item) => item.getAsFile())
          .filter((file): file is File => Boolean(file))
        if (!files.length) return
        event.preventDefault()
        const context = reportRequestContext()
        for (const file of files.slice(0, 3 - state.images.length)) {
          if (!alive.current) break
          try {
            assertReportRequest(context)
          } catch {
            break
          }
          await controller.addImage(file)
        }
      }}
    >
      {!available && (
        <p role="status" className="text-sm">
          {t.unavailable}
        </p>
      )}
      {state.report && (
        <div
          role="status"
          className="space-y-2 rounded-[var(--radius-control)] border border-[var(--color-border)] p-3"
        >
          <p className="font-medium">
            {t.saved} · {state.report.reference}
          </p>
          <p className="text-xs">
            {state.images.some(image => image.kind === 'video') ? t.mediaSummary : t.summary}: {state.summary.attached} / {state.summary.failed} /{' '}
            {state.summary.pending}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onView(state.report!.id)}
          >
            {t.view}
          </Button>
        </div>
      )}
      {['pending', 'unknown'].includes(state.outcome) && (
        <p role="status" className="text-sm">
          {t.pending}
        </p>
      )}
      {state.error && !invalidDescription && (
        <p role="alert" className="text-sm">
          {state.error.code === 'image_preparation_failed'
              ? t.imageFailed
              : state.error.code === 'video_preparation_failed'
                ? t.videoFailed
              : state.error.status === 429
                ? t.quota
                : t.error}
          {state.error.retryAfter !== undefined && (
            <>
              {' '}
              {t.wait} {state.error.retryAfter}
            </>
          )}
        </p>
      )}
      <fieldset disabled={!editable}>
        <legend className="mb-2 text-sm font-medium">{t.kind}</legend>
        <div className="grid grid-cols-2 gap-2">
          {(['bug', 'improvement'] as const).map((kind) => (
            <label
              key={kind}
              className={`flex min-h-14 items-center gap-2 rounded-[var(--radius-control)] border px-3 py-2 text-sm has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 ${state.draft.kind === kind ? 'border-[var(--color-text)] bg-[var(--color-surface-muted)]' : 'border-[var(--color-border)]'}`}
            >
              <input
                type="radio"
                name="report-kind"
                checked={state.draft.kind === kind}
                onChange={() => controller.updateDraft({ kind })}
                className="sr-only"
              />
              <PexIcon
                name={kind === 'bug' ? 'warning' : 'write'}
                size="sm"
                tone={state.draft.kind === kind ? 'strong' : 'muted'}
              />
              {t[kind]}
            </label>
          ))}
        </div>
      </fieldset>
      <div>
        <label
          htmlFor="report-description"
          className="block text-sm font-medium"
        >
          {t.description}
        </label>
        <p
          id="report-description-help"
          className="mb-2 mt-1 text-xs leading-5 text-[var(--color-text-muted)]"
        >
          {t.descriptionHelp}
        </p>
        <textarea
          ref={descriptionRef}
          id="report-description"
          aria-describedby={`report-description-help${invalidDescription ? ' report-description-error' : ''}`}
          aria-invalid={invalidDescription}
          disabled={!editable}
          value={state.draft.description}
          onChange={(event) =>
            controller.updateDraft({ description: event.target.value })
          }
          placeholder={t.placeholder}
          className={`min-h-28 resize-y ${reportInputClass}`}
        />
        {invalidDescription && (
          <p id="report-description-error" role="alert" className="mt-1 text-sm text-[var(--color-danger)]">
            {t.invalid}
          </p>
        )}
        <div className="mt-1 flex justify-between text-xs text-[var(--color-text-muted)]">
          <span>{t.descriptionMinimum}</span>
          <span>{Array.from(state.draft.description).length} / 5,000</span>
        </div>
      </div>
      <ReportImages
        controller={controller}
        state={state}
        editable={editable}
        approved={approved}
        onApproval={onApproval}
        onRecord={onRecord}
        recordingBusy={recordingBusy}
        recordingErrorMessage={recordingErrorMessage}
      />
      <fieldset disabled={!editable} className="space-y-2.5">
        <legend className="mb-2 text-sm font-medium">
          {t.impact}{' '}
          <span className="font-normal text-[var(--color-text-muted)]">
            · {t.optional}
          </span>
        </legend>
        {(['blocking', 'difficult', 'minor'] as const).map((impact) => (
          <label
            key={impact}
            className="flex min-h-8 items-center gap-2.5 text-sm"
          >
            <input
              type="radio"
              name="report-impact"
              checked={state.draft.impact === impact}
              onChange={() => controller.updateDraft({ impact })}
              className="h-4 w-4 accent-[var(--color-text)]"
            />
            {t[impact]}
          </label>
        ))}
        {state.draft.impact && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => controller.updateDraft({ impact: undefined })}
          >
            {t.clearImpact}
          </Button>
        )}
      </fieldset>
      <details className="rounded-[var(--radius-control)] border border-[var(--color-border)] px-3 py-2.5 text-xs text-[var(--color-text-muted)]">
        <summary className="cursor-pointer text-sm font-medium text-[var(--color-text)]">
          {t.context}
        </summary>
        <p className="mt-2 leading-5">{t.contextHelp}</p>
      </details>
    </div>
  )
}
